import React, { useMemo, useReducer, useRef } from 'react'
import { GenericError, Text, Flex, FlatButton } from '@sketch/components'
import { useTreeState } from '../state'
import { useTreeSearch } from './useTreeSearch'
import {
  CollapsibleTree,
  TreeSearch,
  useTreeKeyboardNavigation,
} from '../state.base'

import { TreeNodeSkeleton } from '../TreeNode'
import { useOnListChange } from '../state/useOnListChange'
import { TreeContainer } from '../TreeNode.styles'
import { isTruthy, clamp } from '@sketch/utils'
import { InView } from 'react-intersection-observer'
import { Node } from './TreeNode.searchTree'
import { ReactComponent as SearchIcon } from '@sketch/icons/search-64'
import {
  useSearchDestinations,
  TransferDestination,
} from './useSearchDestinations'

const NoResults = () => {
  const { onSearchChange } = useTreeSearch()
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="8px"
      minHeight="100%"
    >
      <Flex alignItems="center" justifyContent="center">
        <SearchIcon height="64px" opacity={0.3} />
      </Flex>
      <Text.Span>No folders matched your search</Text.Span>
      <FlatButton
        variant="primary"
        onClick={() => onSearchChange({ name: '' }, true)}
      >
        Clear
      </FlatButton>
    </Flex>
  )
}

export interface SearchTreeNodesProps {
  search: TreeSearch
}
export const SearchTreeNodes = (props: SearchTreeNodesProps) => {
  const { search } = props
  const treeState = useTreeState()

  const {
    destinations,
    error,
    loading,
    workspaceDestinations,
    loadMore,
    totalCount,
  } = useSearchDestinations(props)

  const [, forceRender] = useReducer(s => s + 1, 0)
  const openedWorkspaces = useMemo(() => new Set<string>(), [])

  useOnListChange(destinations, items => {
    const getParentId = (item: TransferDestination) => {
      if (item.__typename === 'Workspace') return null
      return item.workspace.identifier
    }

    const nodesWithChildren = new Set<string>(
      items.map(getParentId).filter(isTruthy)
    )

    treeState.setChildrenTree({
      list: items,
      itemParams: x => ({
        id: x.identifier,
        parentId: getParentId(x),
        hasChildren: nodesWithChildren.has(x.identifier),
      }),
    })

    for (const workspace of workspaceDestinations) {
      if (!openedWorkspaces.has(workspace.identifier)) {
        treeState.openAllToNode(workspace.identifier)
        openedWorkspaces.add(workspace.identifier)
      }
    }

    forceRender()
  })

  const containerRef = useRef<HTMLDivElement>(null)
  useTreeKeyboardNavigation(containerRef, treeState)

  if (loading) {
    return (
      <div>
        <TreeNodeSkeleton depth={0} />
        <TreeNodeSkeleton depth={1} emptyChevron />
        <TreeNodeSkeleton depth={1} emptyChevron />
        <TreeNodeSkeleton depth={0} />
        <TreeNodeSkeleton depth={0} />
      </div>
    )
  }

  if (error || !destinations) {
    return <GenericError />
  }

  if (destinations.length === 0) {
    return <NoResults />
  }

  const placeholdersCount = clamp(
    (totalCount || 0) - destinations.length,
    0,
    40
  )

  const lastItem = destinations[destinations.length - 1]

  return (
    <>
      <TreeContainer ref={containerRef} role="tree" creatingProject={false}>
        <Node
          id={CollapsibleTree.ROOT_ID}
          depth={0}
          key={search.name}
          search={search.name || ''}
          lastItemId={lastItem?.identifier || ''}
          // here we are loading more items in case user navigated using keyboard to the end of the list
          // but doesn't see any placeholders (yet), this can happen on Firefox
          onLastItemVisible={loadMore}
        />
        {Array.from({ length: placeholdersCount }).map((_, i) => (
          <InView
            // here we are loading more items in case user scrolls to the end of the list
            // and sees only placeholders
            onChange={inView => inView && loadMore()}
            // we are using last item as a key to force re-render placeholders
            // thus ensuring that if user scrolls to the end of the list, where
            // only placeholders are visible, we will still rerender placeholders
            // when the list changes. Forcing to load more items.
            key={lastItem.identifier + i}
          >
            <TreeNodeSkeleton depth={1} emptyChevron />
          </InView>
        ))}
      </TreeContainer>
    </>
  )
}
